import { useCallback, useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { exportFlowSource, getFlowSource, restoreFlowSource } from '@/api/integration'
import type { FlowRestoreRow, FlowSourceStatus } from '@/api/integration'
import { FLOW_SETUP_OPEN_EVENT, announceFlowRestoreStarted } from './FlowSetupDialog'

/**
 * "Flow designs in source" — the line under the Flows section that says the
 * app's IOFlow flows travel with git (`Integration_Design/flows`), and, for an
 * app created from a repository, how restoring its flows went.
 *
 * Restore runs in the background on the server (`ioflow_flow_source`); this
 * panel polls its report while it runs and re-reads the Flows list when done.
 */

const POLL_MS = 3000

const STATUS_TEXT: Record<string, string> = {
  created: 'Restored',
  updated: 'Restored',
  present: 'Already in IOFlow',
  failed: 'Not restored',
  not_restored: 'Waiting for a connector / event',
}

function rowNeedsYou(r: FlowRestoreRow): boolean {
  return ['failed', 'not_restored'].includes(r.status) || !!r.needs_attention
    || !!(r.waiting_for && r.waiting_for.length) || !!(r.secrets_needed && r.secrets_needed.length)
}

interface Props {
  project: string
  canEdit: boolean
  /** Re-read the Flows list (after a restore). */
  onRestored?: () => void
}

function errorMessage(e: unknown, fallback: string): string {
  const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail
  return typeof detail === 'string' ? detail : fallback
}

export default function FlowSourcePanel({ project, canEdit, onRestored }: Props) {
  const [data, setData] = useState<FlowSourceStatus | null>(null)
  const [busy, setBusy] = useState<'' | 'export' | 'restore'>('')
  const [open, setOpen] = useState(false)
  const wasRunning = useRef(false)

  const load = useCallback(async () => {
    if (!project) return
    try {
      setData(await getFlowSource(project))
    } catch {
      setData(null)
    }
  }, [project])

  useEffect(() => {
    load()
  }, [load])

  const status = data?.restore?.status
  const running = status === 'running' || status === 'pending'

  useEffect(() => {
    if (running) {
      wasRunning.current = true
      const t = setTimeout(load, POLL_MS)
      return () => clearTimeout(t)
    }
    if (wasRunning.current) {
      wasRunning.current = false
      onRestored?.()
    }
    return undefined
  }, [running, data, load, onRestored])

  if (!data) return null
  const designs = data.designs || []
  const report = data.restore || {}
  const rows: FlowRestoreRow[] = report.flows || []
  const needsYou = rows.filter(rowNeedsYou)
  const doExport = async () => {
    setBusy('export')
    try {
      const res = await exportFlowSource(project)
      if (res.skipped) toast(res.message || 'Restore the flows first.')
      else if ((res.written || []).length) {
        toast.success(`${res.written.length} flow design(s) saved in the source — commit and push them from Source Control`)
      } else toast.success('The flow designs in the source are up to date')
      await load()
    } catch (e) {
      toast.error(errorMessage(e, 'Could not save the flow designs'))
    } finally {
      setBusy('')
    }
  }

  const doRestore = async () => {
    setBusy('restore')
    try {
      const res = await restoreFlowSource(project)
      if (!res.started) toast(res.message || 'A restore is already running.')
      else announceFlowRestoreStarted(project)
      await load()
    } catch (e) {
      toast.error(errorMessage(e, 'Could not start the restore'))
    } finally {
      setBusy('')
    }
  }

  if (!designs.length && !status && !data.restore_pending) {
    // Nothing kept yet and nothing restored (an app whose flows predate flow
    // designs in source): say where designs go, and offer to write them now.
    return (
      <p style={{ margin: '0 0 0.75rem', fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
        <span className="material-icons" style={{ fontSize: 15, verticalAlign: 'text-bottom', marginRight: 4 }}>source</span>
        Each flow’s design is saved in this app’s source ({data.folder}) when it is saved and on every
        git push, so an app created from the repository gets its flows back.
        {canEdit && (
          <button type="button" className="btn btn-secondary" style={{ padding: '2px 10px', marginLeft: 8 }}
            disabled={!!busy} onClick={doExport}
            title="Write every flow's current design from IOFlow into the source now, so it shows in Source Control.">
            {busy === 'export' ? 'Saving…' : 'Save designs to source'}
          </button>
        )}
      </p>
    )
  }

  const tone = running
    ? { bg: 'var(--color-info-weak, var(--color-surface-alt))', fg: 'var(--color-text)' }
    : status === 'partial' || status === 'failed' || status === 'waiting_for_ioflow' || data.restore_pending
      ? { bg: 'var(--color-warning-weak)', fg: 'var(--color-warning)' }
      : { bg: 'var(--color-surface-alt, transparent)', fg: 'var(--color-text-secondary)' }

  return (
    <div
      data-tw="flow-source"
      style={{
        margin: '0 0 0.75rem', padding: '0.55rem 0.75rem', borderRadius: 8,
        background: tone.bg, color: tone.fg, fontSize: '0.8375rem',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
        {running ? (
          <span className="spinner" style={{ width: 15, height: 15, borderWidth: 2 }} />
        ) : (
          <span className="material-icons" style={{ fontSize: 17 }}>source</span>
        )}
        <span style={{ flex: 1, minWidth: 240 }}>
          {running
            ? 'Restoring this app’s IOFlow flows from its source…'
            : data.restore_pending
              ? 'This app came from a repository and its IOFlow flows are not restored yet.'
              : status && report.message
                ? <>Restored from source: {report.message}</>
                : <>{designs.length} flow design{designs.length === 1 ? '' : 's'} kept in the source ({data.folder}) — pushed with git.</>}
        </span>
        {(needsYou.length > 0 || (report.notes || []).length > 0 || (report.gaps || []).length > 0) && !running && (
          <button type="button" className="btn btn-secondary" style={{ padding: '2px 10px' }} onClick={() => setOpen((o) => !o)}>
            {open ? 'Hide details' : 'Details'}
          </button>
        )}
        {canEdit && !running && report.needs_input && (
          <button type="button" className="btn btn-primary" style={{ padding: '2px 10px' }}
            onClick={() => window.dispatchEvent(new CustomEvent(FLOW_SETUP_OPEN_EVENT, { detail: { project } }))}
            title="Sign in to the applications the flows use and enter the values git does not carry.">
            Finish setup
          </button>
        )}
        {canEdit && !running && (status || data.restore_pending || designs.length > 0) && (
          <button type="button" className="btn btn-secondary" style={{ padding: '2px 10px' }}
            disabled={!!busy} onClick={doRestore}
            title="Create any flow of the source that is not in IOFlow yet. Flows already there are left as they are.">
            {busy === 'restore' ? 'Starting…' : status ? 'Restore again' : 'Restore flows'}
          </button>
        )}
        {canEdit && !running && !data.restore_pending && (
          <button type="button" className="btn btn-secondary" style={{ padding: '2px 10px' }}
            disabled={!!busy} onClick={doExport}
            title="Write every flow's current design from IOFlow into the source now (a git push does this too).">
            {busy === 'export' ? 'Saving…' : 'Save designs to source'}
          </button>
        )}
      </div>
      {open && !running && (
        <ul style={{ margin: '0.5rem 0 0', paddingLeft: '1.25rem', color: 'var(--color-text)' }}>
          {needsYou.map((r) => (
            <li key={r.slug} style={{ marginBottom: 4 }}>
              <strong>{r.name || r.slug}</strong> — {STATUS_TEXT[r.status] || r.status}
              {r.detail ? `: ${r.detail}` : ''}
              {r.needs_attention ? ` ${r.needs_attention}` : ''}
            </li>
          ))}
          {(report.gaps || []).map((g, i) => (
            <li key={`gap-${i}`} style={{ marginBottom: 4 }}>{g.detail || g.kind}</li>
          ))}
          {(report.notes || []).map((n, i) => (
            <li key={`note-${i}`} style={{ marginBottom: 4 }}>{n}</li>
          ))}
        </ul>
      )}
    </div>
  )
}
